import { useState, useEffect, useCallback } from "react";
import {
  Send, Plus, Trash2, Edit3, X, Mail, Bell, Clock, Users, Ban,
  BarChart3, CheckCircle2, AlertTriangle, Loader2, Calendar, Eye,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

const STATUS_STYLES = {
  draft: "bg-slate-700/40 text-slate-300 border-slate-600/40",
  scheduled: "bg-blue-500/15 text-blue-400 border-blue-500/25",
  sending: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  sent: "bg-green-500/15 text-green-400 border-green-500/25",
  cancelled: "bg-slate-700/40 text-slate-400 border-slate-600/40",
  failed: "bg-red-500/15 text-red-400 border-red-500/25",
};

const AUDIENCE_LABELS = {
  all: "All users", premium: "All Premium", paid: "Paid members",
  complimentary: "Complimentary", free: "Free users", expired: "Expired", role: "By role",
};

const CHANNEL_ICONS = { in_app: Bell, email: Mail, both: Send };

const emptyForm = {
  title: "", channel: "in_app", language_mode: "both",
  subject_en: "", subject_fr: "", body_en: "", body_fr: "",
  audience: { type: "all", role: "" },
};

export default function AdminMessages() {
  const [campaigns, setCampaigns] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [composer, setComposer] = useState(null); // {mode:'create'|'edit', campaign}
  const [form, setForm] = useState(emptyForm);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [report, setReport] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/messages`, {
        params: { status: statusFilter, search }, withCredentials: true,
      });
      setCampaigns(r.data.campaigns); setCounts(r.data.counts);
    } catch { }
    setLoading(false);
  }, [statusFilter, search]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => {
    axios.get(`${API}/admin/rbac/roles`, { withCredentials: true }).then(r => setRoles(r.data)).catch(() => {});
  }, []);

  // Poll while any campaign is sending
  useEffect(() => {
    if (!campaigns.some(c => c.status === "sending")) return;
    const t = setInterval(fetchCampaigns, 4000);
    return () => clearInterval(t);
  }, [campaigns, fetchCampaigns]);

  // Live recipient estimate in composer
  useEffect(() => {
    if (!composer) return;
    let active = true;
    axios.post(`${API}/admin/messages/estimate`,
      { audience: form.audience, channel: form.channel }, { withCredentials: true })
      .then(r => { if (active) setEstimate(r.data); }).catch(() => { });
    return () => { active = false; };
  }, [composer, form.audience, form.channel]);

  const openCreate = () => {
    setForm(emptyForm); setScheduleAt(""); setEstimate(null);
    setComposer({ mode: "create" });
  };
  const openEdit = (c) => {
    setForm({
      title: c.title, channel: c.channel, language_mode: c.language_mode,
      subject_en: c.subject_en || "", subject_fr: c.subject_fr || "",
      body_en: c.body_en || "", body_fr: c.body_fr || "",
      audience: { type: c.audience?.type || "all", role: c.audience?.role || "" },
    });
    setScheduleAt(c.scheduled_at ? c.scheduled_at.slice(0, 16) : "");
    setComposer({ mode: "edit", campaign: c });
  };

  const saveDraft = async () => {
    if (!form.title.trim()) { alert("Campaign title is required"); return; }
    setBusy(true);
    try {
      if (composer.mode === "create") {
        await axios.post(`${API}/admin/messages`, form, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/messages/${composer.campaign.id}`, form, { withCredentials: true });
      }
      setComposer(null); fetchCampaigns();
    } catch (e) { alert(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const saveAndSend = async () => {
    if (!form.title.trim()) { alert("Campaign title is required"); return; }
    if (!form.body_en && !form.body_fr) { alert("Add a message body first"); return; }
    setBusy(true);
    try {
      let id = composer.mode === "edit" ? composer.campaign.id : null;
      if (composer.mode === "create") {
        const r = await axios.post(`${API}/admin/messages`, form, { withCredentials: true });
        id = r.data.campaign.id;
      } else {
        await axios.put(`${API}/admin/messages/${id}`, form, { withCredentials: true });
      }
      const body = scheduleAt ? { scheduled_at: new Date(scheduleAt).toISOString() } : {};
      await axios.post(`${API}/admin/messages/${id}/send`, body, { withCredentials: true });
      setComposer(null); fetchCampaigns();
    } catch (e) { alert(e.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };

  const cancelCampaign = async (c) => {
    if (!window.confirm(`Cancel "${c.title}"?`)) return;
    try { await axios.post(`${API}/admin/messages/${c.id}/cancel`, {}, { withCredentials: true }); fetchCampaigns(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const deleteCampaign = async (c) => {
    if (!window.confirm(`Permanently delete "${c.title}" and its delivery report?`)) return;
    try { await axios.delete(`${API}/admin/messages/${c.id}`, { withCredentials: true }); fetchCampaigns(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const statusTabs = [
    { label: "All", value: "" },
    { label: `Draft (${counts.draft || 0})`, value: "draft" },
    { label: `Scheduled (${counts.scheduled || 0})`, value: "scheduled" },
    { label: `Sending (${counts.sending || 0})`, value: "sending" },
    { label: `Sent (${counts.sent || 0})`, value: "sent" },
    { label: `Cancelled (${counts.cancelled || 0})`, value: "cancelled" },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-400">Send bulk in-app and email notifications to a targeted audience.</p>
        </div>
        <button onClick={openCreate} data-testid="new-campaign-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors">
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-4" data-testid="message-status-tabs">
        {statusTabs.map(t => (
          <button key={t.value} onClick={() => setStatusFilter(t.value)}
            className={cn("px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
              statusFilter === t.value ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white")}
            data-testid={`status-tab-${t.value || "all"}`}>
            {t.label}
          </button>
        ))}
        <input type="text" placeholder="Search campaigns..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="ml-auto px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
          data-testid="message-search" />
      </div>

      {/* Campaign list */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="campaigns-table">
        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="animate-spin mx-auto mb-2" /> Loading…</div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center" data-testid="campaigns-empty">
            <Send className="mx-auto mb-3 text-slate-600" size={32} />
            <p className="text-sm text-slate-400">No campaigns yet. Create your first broadcast.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {campaigns.map(c => {
              const ChIcon = CHANNEL_ICONS[c.channel] || Send;
              const s = c.stats || {};
              return (
                <div key={c.id} className="p-4 flex items-center gap-4 hover:bg-slate-800/20" data-testid={`campaign-row-${c.id}`}>
                  <div className="p-2.5 rounded-lg bg-slate-800 flex-shrink-0"><ChIcon size={16} className="text-blue-400" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{c.title}</span>
                      <span className={cn("px-2 py-0.5 rounded-md border text-[11px] font-medium capitalize", STATUS_STYLES[c.status])} data-testid={`campaign-status-${c.id}`}>
                        {c.status === "sending" && <Loader2 size={9} className="inline animate-spin mr-1" />}{c.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1"><Users size={11} /> {AUDIENCE_LABELS[c.audience?.type] || c.audience?.type}{c.audience?.type === "role" && c.audience?.role ? `: ${c.audience.role}` : ""}</span>
                      <span>~{c.estimated_recipients} recipients</span>
                      {c.status === "scheduled" && c.scheduled_at && (
                        <span className="flex items-center gap-1 text-blue-400"><Calendar size={11} /> {new Date(c.scheduled_at).toLocaleString()}</span>
                      )}
                      {(c.status === "sent" || c.status === "sending") && (
                        <span>{s.in_app_sent || 0} in-app · {s.email_sent || 0} email{s.email_not_configured ? ` · ${s.email_not_configured} email skipped (no key)` : ""}{s.email_failed ? ` · ${s.email_failed} failed` : ""}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {(c.status === "sent" || c.status === "sending") && (
                      <button onClick={() => setReport(c)} title="Delivery report" data-testid={`report-${c.id}`}
                        className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><BarChart3 size={15} /></button>
                    )}
                    {(c.status === "draft" || c.status === "scheduled") && (
                      <button onClick={() => openEdit(c)} title="Edit" data-testid={`edit-${c.id}`}
                        className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><Edit3 size={15} /></button>
                    )}
                    {(c.status === "scheduled" || c.status === "sending") && (
                      <button onClick={() => cancelCampaign(c)} title="Cancel" data-testid={`cancel-${c.id}`}
                        className="p-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400"><Ban size={15} /></button>
                    )}
                    {c.status !== "sending" && (
                      <button onClick={() => deleteCampaign(c)} title="Delete" data-testid={`delete-${c.id}`}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 size={15} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer */}
      {composer && (
        <Composer
          form={form} setForm={setForm} roles={roles} estimate={estimate}
          scheduleAt={scheduleAt} setScheduleAt={setScheduleAt} busy={busy}
          mode={composer.mode}
          onClose={() => setComposer(null)}
          onSaveDraft={saveDraft} onSaveSend={saveAndSend} />
      )}

      {/* Delivery report */}
      {report && <ReportModal campaign={report} onClose={() => setReport(null)} />}
    </>
  );
}

// ===== Composer =====
function Composer({ form, setForm, roles, estimate, scheduleAt, setScheduleAt, busy, mode, onClose, onSaveDraft, onSaveSend }) {
  const [lang, setLang] = useState("en");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAud = (k, v) => setForm(f => ({ ...f, audience: { ...f.audience, [k]: v } }));
  const channels = [
    { v: "in_app", label: "In-app", icon: Bell }, { v: "email", label: "Email", icon: Mail }, { v: "both", label: "Both", icon: Send },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="composer-overlay">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <h3 className="text-base font-semibold text-white">{mode === "create" ? "New Campaign" : "Edit Campaign"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Campaign title (internal)</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} data-testid="composer-title"
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="e.g. July Premium Offer" />
          </div>

          {/* Channel */}
          <div>
            <label className="text-xs text-slate-500 block mb-1.5">Delivery channel</label>
            <div className="flex gap-2">
              {channels.map(c => (
                <button key={c.v} onClick={() => set("channel", c.v)} data-testid={`channel-${c.v}`}
                  className={cn("flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors",
                    form.channel === c.v ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white")}>
                  <c.icon size={14} /> {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div>
            <label className="text-xs text-slate-500 block mb-1.5">Audience</label>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(AUDIENCE_LABELS).map(([v, label]) => (
                <button key={v} onClick={() => setAud("type", v)} data-testid={`audience-${v}`}
                  className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                    form.audience.type === v ? "bg-emerald-600 border-emerald-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white")}>
                  {label}
                </button>
              ))}
            </div>
            {form.audience.type === "role" && (
              <select value={form.audience.role} onChange={e => setAud("role", e.target.value)} data-testid="audience-role-select"
                className="mt-2 w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="">Select a role…</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            {estimate && (
              <div className="mt-2 text-xs text-slate-400 flex items-center gap-2" data-testid="estimate-line">
                <Users size={12} className="text-emerald-400" />
                <span><b className="text-white">{estimate.total}</b> recipients
                  {(form.channel === "email" || form.channel === "both") && <> · <b className="text-white">{estimate.with_email}</b> with email{estimate.without_email > 0 ? ` · ${estimate.without_email} without email` : ""}</>}
                </span>
              </div>
            )}
          </div>

          {/* Language versions */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-slate-500">Message content</label>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Send:</span>
                <div className="flex gap-1">
                  {[["both", "FR + EN"], ["en", "EN only"], ["fr", "FR only"]].map(([v, l]) => (
                    <button key={v} onClick={() => set("language_mode", v)} data-testid={`langmode-${v}`}
                      className={cn("px-2 py-1 rounded text-[11px] font-medium",
                        form.language_mode === v ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white")}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-1.5 mb-2">
              {[["en", "English"], ["fr", "Français"]].map(([v, l]) => (
                <button key={v} onClick={() => setLang(v)} data-testid={`lang-tab-${v}`}
                  className={cn("px-3 py-1.5 rounded-t-lg text-xs font-medium border-b-2 transition-colors",
                    lang === v ? "border-blue-500 text-white" : "border-transparent text-slate-500 hover:text-white")}>{l}</button>
              ))}
            </div>
            {lang === "en" ? (
              <div className="space-y-2">
                <input value={form.subject_en} onChange={e => set("subject_en", e.target.value)} data-testid="subject-en"
                  placeholder="Subject / title (EN)" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                <textarea rows={5} value={form.body_en} onChange={e => set("body_en", e.target.value)} data-testid="body-en"
                  placeholder="Message body (EN). Basic HTML allowed for email." className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-y" />
              </div>
            ) : (
              <div className="space-y-2">
                <input value={form.subject_fr} onChange={e => set("subject_fr", e.target.value)} data-testid="subject-fr"
                  placeholder="Sujet / titre (FR)" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                <textarea rows={5} value={form.body_fr} onChange={e => set("body_fr", e.target.value)} data-testid="body-fr"
                  placeholder="Corps du message (FR). HTML simple autorisé pour l'email." className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-y" />
              </div>
            )}
          </div>

          {/* Schedule */}
          <div>
            <label className="text-xs text-slate-500 block mb-1 flex items-center gap-1"><Clock size={12} /> Schedule (optional — leave empty to send immediately)</label>
            <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} data-testid="schedule-at"
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
          </div>

          {(form.channel === "email" || form.channel === "both") && estimate?.with_email === 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-300 flex items-center gap-2">
              <AlertTriangle size={14} /> No recipients in this audience have an email address.
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onSaveDraft} disabled={busy} data-testid="save-draft-btn"
              className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
              {busy ? "Saving…" : "Save Draft"}
            </button>
            <button onClick={onSaveSend} disabled={busy} data-testid="send-campaign-btn"
              className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : (scheduleAt ? <Calendar size={15} /> : <Send size={15} />)}
              {scheduleAt ? "Schedule" : "Send Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Delivery report =====
function ReportModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    axios.get(`${API}/admin/messages/${campaign.id}/deliveries`, { params: { page, limit: 25 }, withCredentials: true })
      .then(r => setData(r.data)).catch(() => { });
  }, [campaign.id, page]);
  const s = data?.stats || campaign.stats || {};
  const cards = [
    { label: "Recipients", value: s.total || 0, icon: Users, c: "text-blue-400" },
    { label: "In-app sent", value: s.in_app_sent || 0, icon: Bell, c: "text-emerald-400" },
    { label: "Email sent", value: s.email_sent || 0, icon: Mail, c: "text-green-400" },
    { label: "Email failed", value: (s.email_failed || 0) + (s.email_not_configured || 0), icon: AlertTriangle, c: "text-amber-400" },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="report-overlay">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white flex items-center gap-2"><BarChart3 size={16} /> {campaign.title} — Delivery Report</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {cards.map(c => (
              <div key={c.label} className="bg-slate-950 rounded-xl p-3 border border-slate-800">
                <c.icon size={14} className={c.c} />
                <div className="text-xl font-bold text-white mt-1">{c.value}</div>
                <div className="text-[11px] text-slate-500">{c.label}</div>
              </div>
            ))}
          </div>
          {s.email_not_configured > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 mb-4">
              {s.email_not_configured} email(s) not sent — no Resend API key configured. In-app delivery unaffected.
            </div>
          )}
          <div className="border border-slate-800 rounded-xl overflow-hidden" data-testid="deliveries-list">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-800 text-left text-xs text-slate-500">
                <th className="px-3 py-2">Recipient</th><th className="px-3 py-2">In-app</th><th className="px-3 py-2">Email</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800/60">
                {(data?.deliveries || []).map(d => (
                  <tr key={d.id} className="text-slate-300">
                    <td className="px-3 py-2 truncate max-w-[220px]">{d.name || d.email || "—"}</td>
                    <td className="px-3 py-2"><StatusPill v={d.in_app_status} /></td>
                    <td className="px-3 py-2"><StatusPill v={d.email_status} /></td>
                  </tr>
                ))}
                {data && data.deliveries.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">No deliveries recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data && data.pages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <span>Page {data.page} of {data.pages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded bg-slate-800 disabled:opacity-40">Prev</button>
                <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded bg-slate-800 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ v }) {
  const map = {
    sent: "bg-green-500/15 text-green-400", skipped: "bg-slate-700/40 text-slate-400",
    failed: "bg-red-500/15 text-red-400", email_not_configured: "bg-amber-500/15 text-amber-400",
    no_email: "bg-slate-700/40 text-slate-500",
  };
  const label = { email_not_configured: "no key", no_email: "no email" }[v] || v;
  return <span className={cn("px-2 py-0.5 rounded-md text-[11px] font-medium", map[v] || "bg-slate-700/40 text-slate-400")}>{label}</span>;
}
